import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ChevronRight,
  Check,
  Search,
  Video,
  VideoOff,
  Users,
  Plus,
  Trash2,
  Bot,
  UserRound,
  Mail,
  Link,
  ClipboardList,
  Globe,
  Hash,
  Lock,
  FileText,
  Paperclip,
  Eye,
  Pencil,
  Lightbulb,
  Briefcase,
  Code,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  X
} from "lucide-react";
import { motion } from "framer-motion";
import ScheduleClient from "../data/api/ScheduleClient";
import JobOpportunityClient from "../../jobOpportunity/data/api/JobOpportunityClient";
import { CompanyUser } from "../data/interfaces/ScheduleInterfaces";
import Loader from "../../../shared/components/Loader";
import Alert from "../../../shared/components/Alert";
import DateTimePicker from "../../../shared/components/DateTimePicker";
import { CustomDropdown } from "../../../shared/components/CustomDropdown";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../../shared/components/Table/Table";
import {
  WORLD_CLOCK_CITIES,
  zonedLocalToUtcDate,
  utcDateToZonedLocal,
  formatUtcInTimezone,
} from "../utils/timezoneUtils";
import "../styles/ScheduleStyles.css";
import TimezoneSelect from "react-timezone-select";
import { GlobeTimezoneModal } from "../../../shared/components/GlobeTimezoneModal";
import { RichTextEditor } from "../../../shared/components/RichTextEditor/RichTextEditor";

interface Round {
  round_id: string;
  name: string;
  round_type: string;
  seq_no: number;
  description?: string;
}

const formatLocalDateTimeString = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ScheduleInterviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = location.state?.id || queryParams.get("id");
  const isViewMode = !!(
    location.state?.view || location.pathname.includes("/view")
  );
  const jobOpportunityId = location.state?.jobOpportunityId;
  const returnUrl = location.state?.returnUrl || "/dashboard/interviews";

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchUser, setSearchUser] = useState("");
  const [alert, setAlert] = useState({
    show: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });
  const [rounds, setRounds] = useState<Round[]>([]);
  const [status, setStatus] = useState<string>("");
  const [isGlobeModalOpen, setIsGlobeModalOpen] = useState(false);
  const [expertMeetingOption, setExpertMeetingOption] = useState<
    "create" | "add"
  >("create");
  const [selectedDescFile, setSelectedDescFile] = useState<File | null>(null);
  
  // Job opportunities for context dropdown
  const [jobOpportunities, setJobOpportunities] = useState<any[]>([]);
  const [isJdModalOpen, setIsJdModalOpen] = useState(false);
  const [skillDiff, setSkillDiff] = useState("Medium");
  const [skillInput, setSkillInput] = useState("");
  const [editingSkillIdx, setEditingSkillIdx] = useState<number | null>(null);

  const programmingLanguages = [
    "Python",
    "Java",
    "JavaScript",
    "C",
    "C++",
    "Go",
  ];

  const [form, setForm] = useState({
    sessionName: "",
    roundId: "",
    startDateTime: "",
    endDateTime: "",
    videoRecording: false,
    transcriptionEnabled: true,
    duration: 60,
    interviewType: "AI" as "AI" | "EXPERT",
    interviewerEmail: "",
    meetingLink: "",
    meetingId: "",
    meetingPassword: "",
    jobDescription: "",
    descriptionFileName: "",
    descriptionFileS3Key: "",
    descriptionFilePresignedUrl: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    programmingLanguage: [] as string[],
    noOfCodingQuestion: 5,
    difficultyCount: { Easy: 2, Medium: 2, Hard: 1 } as Record<string, number>,
    codingMode: "all_languages" as "all_languages" | "per_language",
    languageQuestionConfig: [] as {
      language: string;
      question_count: number;
      difficulty_count?: Record<string, number>;
    }[],
    sqlEnabled: false,
    sqlQuestionCount: 5,
    sqlDifficultyCount: { Easy: 2, Medium: 2, Hard: 1 } as Record<
      string,
      number
    >,
    interviewContextJobOpportunityId: jobOpportunityId || "",
    interviewContextSkills: [] as { skill: string; difficulty: string }[],
    interviewContextSeniorDeepDive: true,
    interviewContextCustomInstructions: "",
  });

  const [pendingLang, setPendingLang] = useState("");
  const [pendingCount, setPendingCount] = useState(3);
  const [pendingDiff, setPendingDiff] = useState({
    Easy: 1,
    Medium: 1,
    Hard: 1,
  });

  const selectedRound = rounds.find((r) => r.round_id === form.roundId);
  const selectedJo = useMemo(
    () => jobOpportunities.find((j) => j.opportunity_id === form.interviewContextJobOpportunityId),
    [jobOpportunities, form.interviewContextJobOpportunityId]
  );
  const isCodingRound =
    selectedRound?.round_type?.toUpperCase() === "CODING" ||
    selectedRound?.name?.toLowerCase().includes("coding");
    
  const isTechnicalRound =
    selectedRound?.round_type?.toUpperCase() === "TECHNICAL" ||
    selectedRound?.name?.toLowerCase().includes("technical");
  const showContextStep = form.interviewType === "AI" && isTechnicalRound;

  const isActive = status === "active" || status === "in_progress";

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      // Load Users
      try {
        let initialUsers: any[] = [];
        const selectedEmails = location.state?.selectedEmails;
        if (selectedEmails) {
          try {
            const selectedRes = await ScheduleClient.companyUsers({
              emails: Array.isArray(selectedEmails) ? selectedEmails.join(',') : selectedEmails,
              limit: 100,
              status: "Active",
            });
            const data = selectedRes?.data || selectedRes;
            initialUsers = Array.isArray(data) ? data : data?.records || [];

            const validEmails = initialUsers
              .map((u: any) => u.email_address)
              .filter(Boolean);
            setSelectedUsers((prev) =>
              Array.from(new Set([...prev, ...validEmails])),
            );
          } catch (e) {
            console.error("Failed to load pre-selected users", e);
          }
        }

        let combined = [...initialUsers];

        if (!selectedEmails || selectedEmails.length === 0) {
          const users = await ScheduleClient.companyUsers({
            limit: 100,
            page_number: 1,
            status: "Active",
          });
          const data = users?.data || users; // Fallback for nested data
          const raw = Array.isArray(data) ? data : data?.records || [];

          for (const u of raw) {
            if (!combined.some((c) => c.email_address === u.email_address)) {
              combined.push(u);
            }
          }
          if (raw.length < 100) setHasMoreUsers(false);
        } else {
          setHasMoreUsers(false);
        }

        setCompanyUsers(
          combined.map((u: any) => ({
            id: u.candidate_id || u.email_address || String(Math.random()),
            name:
              [u.first_name, u.last_name].filter(Boolean).join(" ") ||
              u.email_address ||
              "Unknown",
            email: u.email_address || "",
            role: u.role || "Participant",
            department: u.education || u.department || "",
          })),
        );
        setPageNumber(1);
      } catch (err) {
        console.error("Failed to load users:", err);
      }

      // Load Rounds
      try {
        const roundsData = await ScheduleClient.listRounds();
        const data = roundsData?.data || roundsData;
        const fetchedRounds: Round[] = (
          data?.rounds ||
          data?.records ||
          []
        ).sort((a: Round, b: Round) => a.seq_no - b.seq_no);
        setRounds(fetchedRounds);
      } catch (err) {
        console.error("Failed to load rounds:", err);
      }

      // Load Job Opportunities
      try {
        const joData = await JobOpportunityClient.listOpportunities({ limit: 100, status: "Open" });
        if (joData && joData.records) {
          setJobOpportunities(joData.records);
        }
      } catch (err) {
        console.error("Failed to load job opportunities:", err);
      }

      // Load Schedule (if edit mode)
      if (id) {
        try {
          const schedule = await ScheduleClient.get(id);
          const data = schedule?.data || schedule;
          if (data) {
            const rawInterviewType = (data.interview_type || "AI") as
              | "AI"
              | "EXPERT";
            const hydratedTz =
              data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            setForm((prev) => ({
              ...prev,
              sessionName: data.sessionName || data.session_name || "",
              roundId: data.roundId || data.round_id || "",
              startDateTime:
                data.startDateTime || data.start_datetime
                  ? utcDateToZonedLocal(
                      data.start_datetime || data.startDateTime,
                      hydratedTz,
                    )
                  : "",
              endDateTime:
                data.endDateTime || data.end_datetime
                  ? utcDateToZonedLocal(
                      data.end_datetime || data.endDateTime,
                      hydratedTz,
                    )
                  : "",
              videoRecording:
                data.is_video_needed ?? data.videoRecording ?? false,
              transcriptionEnabled:
                data.is_transcription_needed ??
                data.transcriptionEnabled ??
                true,
              duration: data.duration || 60,
              interviewType: rawInterviewType,
              interviewerEmail:
                data.interviewer_email || data.interviewerEmail || "",
              meetingLink: data.meeting_link || data.meetingLink || "",
              meetingId: data.meeting_id || data.meetingId || "",
              meetingPassword:
                data.meeting_password || data.meetingPassword || "",
              jobDescription: data.job_description || data.jobDescription || "",
              descriptionFileName:
                data.description_file_name || data.descriptionFileName || "",
              descriptionFileS3Key:
                data.description_file_s3_key || data.descriptionFileS3Key || "",
              descriptionFilePresignedUrl:
                data.description_file_presigned_url ||
                data.descriptionFilePresignedUrl ||
                "",
              timezone: hydratedTz,
              programmingLanguage: data.programming_language || [],
              noOfCodingQuestion: data.no_of_coding_question || 5,
              difficultyCount: data.difficulty_count || {
                Easy: 2,
                Medium: 2,
                Hard: 1,
              },
              codingMode: data.coding_mode || "all_languages",
              languageQuestionConfig: data.language_question_config || [],
              sqlEnabled: data.sql_enabled || false,
              sqlQuestionCount: data.sql_question_count || 5,
              sqlDifficultyCount: data.sql_difficulty_count || {
                Easy: 2,
                Medium: 2,
                Hard: 1,
              },
            }));
            const targets = data.target_users || data.targetUsers || [];
            if (targets.length > 0) {
              setSelectedUsers(targets);
            } else if (data.assignedUsers) {
              setSelectedUsers(
                data.assignedUsers.map((u: any) => u.email || u.id),
              );
            }
            setStatus(data.status?.toLowerCase() || "");
            
            // Hydrate Interview Context
            if (data.job_opportunity_id || data.context_skills) {
              setForm(prev => ({
                ...prev,
                interviewContextJobOpportunityId: data.job_opportunity_id || "",
                interviewContextSkills: data.context_skills || [],
                interviewContextSeniorDeepDive: data.context_senior_deep_dive || false,
                interviewContextCustomInstructions: data.context_custom_instructions || "",
              }));
            }
          }
        } catch (err) {
          console.error("Failed to load schedule:", err);
        }
      }

      setLoading(false);
    };
    loadData();
  }, [id]);

  const filteredUsers = useMemo(() => {
    if (!searchUser.trim()) return companyUsers;
    const q = searchUser.toLowerCase();
    return companyUsers.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.department.toLowerCase().includes(q),
    );
  }, [companyUsers, searchUser]);

  const loadMoreUsers = async () => {
    if (loadingMore || !hasMoreUsers || searchUser.trim()) return;
    setLoadingMore(true);
    try {
      const nextPage = pageNumber + 1;
      const res = await ScheduleClient.companyUsers({
        limit: 100,
        page_number: nextPage,
        status: "Active",
      });
      const data = res?.data || res;
      const raw = Array.isArray(data) ? data : data?.records || [];
      if (raw.length < 100) setHasMoreUsers(false);

      const newUsers = raw.map((u: any) => ({
        id: u.candidate_id || u.email_address || String(Math.random()),
        name:
          [u.first_name, u.last_name].filter(Boolean).join(" ") ||
          u.email_address ||
          "Unknown",
        email: u.email_address || "",
        role: u.role || "Participant",
        department: u.education || u.department || "",
      }));

      setCompanyUsers((prev) => {
        const combined = [...prev];
        for (const u of newUsers) {
          if (!combined.some((c) => c.email === u.email)) {
            combined.push(u);
          }
        }
        return combined;
      });
      setPageNumber(nextPage);
    } catch (err) {
      console.error("Failed to load more users", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      loadMoreUsers();
    }
  };

  const roundOptions = useMemo(
    () =>
      rounds.map((r) => ({
        value: r.round_id,
        label: r.name,
        description: r.description,
      })),
    [rounds],
  );

  const toggleUser = (email: string) => {
    if (form.interviewType === "EXPERT") {
      setSelectedUsers([email]);
    } else {
      setSelectedUsers((prev) =>
        prev.includes(email)
          ? prev.filter((e) => e !== email)
          : [...prev, email],
      );
    }
  };

  const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (form.interviewType === "EXPERT") return;
    if (e.target.checked) {
      const allEmails = filteredUsers.map((u) => u.email);
      setSelectedUsers((prev) => Array.from(new Set([...prev, ...allEmails])));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSubmit = async () => {
    if (!form.sessionName.trim()) {
      return setAlert({
        show: true,
        title: "Validation Error",
        message: "Session name is required.",
        type: "error",
      });
    }
    if (!form.startDateTime || !form.endDateTime) {
      return setAlert({
        show: true,
        title: "Validation Error",
        message: "Start and end date/time are required.",
        type: "error",
      });
    }
    if (new Date(form.endDateTime) <= new Date(form.startDateTime)) {
      return setAlert({
        show: true,
        title: "Validation Error",
        message: "End time must be after start time.",
        type: "error",
      });
    }
    if (selectedUsers.length === 0) {
      return setAlert({
        show: true,
        title: "Validation Error",
        message: "Please assign at least one participant.",
        type: "error",
      });
    }

    const isHuman = form.interviewType === "EXPERT";
    let formattedInterviewerEmails = "";
    if (isHuman) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const rawInterviewerInput = form.interviewerEmail.trim();
      const parsedEmails = rawInterviewerInput
        ? rawInterviewerInput
            .split(/[,;\s]+/)
            .map((e) => e.trim())
            .filter(Boolean)
        : [];
      if (
        parsedEmails.length === 0 ||
        !parsedEmails.every((email) => emailRegex.test(email))
      ) {
        return setAlert({
          show: true,
          title: "Validation Error",
          message:
            "Please enter valid interviewer email(s) separated by commas.",
          type: "error",
        });
      }
      formattedInterviewerEmails = parsedEmails.join(", ");
      if (!id && expertMeetingOption === "add" && !form.meetingLink.trim()) {
        return setAlert({
          show: true,
          title: "Validation Error",
          message: "Please enter a meeting link.",
          type: "error",
        });
      }
    }

    if (form.sqlEnabled) {
      const sqlSum =
        form.sqlDifficultyCount.Easy +
        form.sqlDifficultyCount.Medium +
        form.sqlDifficultyCount.Hard;
      if (sqlSum !== form.sqlQuestionCount) {
        return setAlert({
          show: true,
          title: "Validation Error",
          message: `SQL difficulty counts (${sqlSum}) must equal the total SQL question count (${form.sqlQuestionCount}).`,
          type: "error",
        });
      }
    }

    setSubmitting(true);
    try {
      const selectedRound = rounds.find((r) => r.round_id === form.roundId);
      const isCoding =
        !isHuman &&
        (selectedRound?.round_type?.toUpperCase() === "CODING" ||
          selectedRound?.name?.toLowerCase().includes("coding"));
      const payload: any = {
        session_name: form.sessionName,
        round_id: form.roundId || undefined,
        round_type: selectedRound?.round_type || "TECHNICAL",
        start_datetime: zonedLocalToUtcDate(form.startDateTime, form.timezone),
        end_datetime: zonedLocalToUtcDate(form.endDateTime, form.timezone),
        timezone: form.timezone,
        is_video_needed: form.videoRecording,
        is_transcription_needed: form.transcriptionEnabled,
        duration: form.duration,
        interview_type: form.interviewType,
        target_users: selectedUsers,
        job_opportunity_id: jobOpportunityId || undefined,
      };
      if (isHuman) {
        payload.interviewer_email = formattedInterviewerEmails;
        if (!id && expertMeetingOption === "create") {
          payload.meeting_link = "";
          payload.meeting_id = "";
          payload.meeting_password = "";
        } else {
          payload.meeting_link = form.meetingLink.trim();
          payload.meeting_id = form.meetingId.trim();
          payload.meeting_password = form.meetingPassword.trim();
        }
        payload.job_description = form.jobDescription.trim();

        if (selectedDescFile) {
          try {
            const uploadRes = await ScheduleClient.uploadDescriptionFile(
              selectedDescFile,
              id || undefined,
              form.descriptionFileS3Key || undefined,
            );
            if (uploadRes && uploadRes.description_file_s3_key) {
              payload.description_file_name = uploadRes.description_file_name;
              payload.description_file_s3_key =
                uploadRes.description_file_s3_key;
            }
          } catch (uploadErr: any) {
            console.error("Description file upload failed:", uploadErr);
          }
        } else if (form.descriptionFileName && form.descriptionFileS3Key) {
          payload.description_file_name = form.descriptionFileName;
          payload.description_file_s3_key = form.descriptionFileS3Key;
        } else {
          payload.description_file_name = "";
          payload.description_file_s3_key = "";
        }
      } else if (isCoding) {
        payload.programming_language = form.programmingLanguage;
        payload.no_of_coding_question = form.noOfCodingQuestion;
        payload.difficulty_count = form.difficultyCount;
        if (
          form.codingMode === "per_language" &&
          form.languageQuestionConfig.length > 0
        ) {
          payload.language_question_config = form.languageQuestionConfig;
          payload.coding_mode = "per_language";
        } else {
          payload.language_question_config = [];
          payload.coding_mode = "all_languages";
        }
        payload.sql_enabled = form.sqlEnabled;
        if (form.sqlEnabled) {
          payload.sql_question_count = form.sqlQuestionCount;
          payload.sql_difficulty_count = form.sqlDifficultyCount;
        }
      }

      if (form.interviewType === "AI") {
        payload.job_opportunity_id = form.interviewContextJobOpportunityId || null;
        payload.context_skills = form.interviewContextSkills || [];
        payload.context_senior_deep_dive = form.interviewContextSeniorDeepDive || false;
        payload.context_custom_instructions = form.interviewContextCustomInstructions || "";
      }

      let res: any;
      if (id) {
        res = await ScheduleClient.update(id, payload);
      } else {
        res = await ScheduleClient.create(payload);
      }

      if (res && res.error) {
        setAlert({
          show: true,
          title: "Error",
          message: res.error,
          type: "error",
        });
        return;
      }

      setAlert({
        show: true,
        title: "Success",
        message: `Interview ${id ? "updated" : "created"} successfully!`,
        type: "success",
      });
      setTimeout(() => navigate(returnUrl), 1500);
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        "Failed to save schedule.";
      setAlert({
        show: true,
        title: "Error",
        message: errorMsg,
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const distributeCount = (total: number) => {
    const base = Math.floor(total / 3);
    const rem = total % 3;
    return {
      Easy: base + (rem >= 1 ? 1 : 0),
      Medium: base + (rem >= 2 ? 1 : 0),
      Hard: base,
    };
  };

  const configuredLangs = form.languageQuestionConfig.map((c) => c.language);
  const availableLangs = programmingLanguages.filter(
    (l) => !configuredLangs.includes(l),
  );

  const handleAddLangConfig = () => {
    if (!pendingLang) return;
    const sum = pendingDiff.Easy + pendingDiff.Medium + pendingDiff.Hard;
    if (sum !== pendingCount) {
      setAlert({
        show: true,
        title: "Validation Error",
        message: `Difficulty counts (${sum}) must equal the total question count (${pendingCount}). Adjust Easy, Medium, and Hard values to sum to ${pendingCount}.`,
        type: "error",
      });
      return;
    }
    if (pendingCount < 1) {
      setAlert({
        show: true,
        title: "Validation Error",
        message: "Question count must be at least 1.",
        type: "error",
      });
      return;
    }
    setForm((prev) => ({
      ...prev,
      languageQuestionConfig: [
        ...prev.languageQuestionConfig,
        {
          language: pendingLang,
          question_count: pendingCount,
          difficulty_count: pendingDiff,
        },
      ],
    }));
    setPendingLang("");
    setPendingCount(3);
    setPendingDiff({ Easy: 1, Medium: 1, Hard: 1 });
  };

  return (
    <motion.div
      className="is-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Loader
        isLoading={loading || submitting}
        message={submitting ? "Saving..." : "Loading..."}
      />
      <Alert
        show={alert.show}
        title={alert.title}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
        primaryButtonText="OK"
        onPrimaryClick={() => {
          setAlert((prev) => ({ ...prev, show: false }));
          if (alert.type === "success") navigate(returnUrl);
        }}
      />

      {/* ── Hero ── */}
      <div className="lp-hero" style={{ marginBottom: "24px" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div className="lp-hero-left" style={{ alignItems: "flex-start" }}>
            <button
              className="btn-ghost is-back-btn"
              onClick={() => navigate(returnUrl)}
              style={{ padding: "8px", height: "auto", borderRadius: "10px" }}
            >
              <ArrowLeft size={20} />
            </button>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "16px",
                paddingTop: "2px",
              }}
            >
              <h1 style={{ margin: 0 }}>
                {isViewMode
                  ? "View Interview"
                  : id
                    ? "Edit Interview"
                    : "Schedule Interview"}
              </h1>
              <span className="lp-hero-chip" style={{ display: "inline-flex" }}>
                {isViewMode
                  ? "View interview schedule details"
                  : id
                    ? "Update the interview schedule"
                    : "Create a new interview schedule"}
              </span>
            </div>
          </div>
          {isViewMode && status !== "closed" && (
            <button
              type="button"
              onClick={() => navigate(`/dashboard/interviews/schedule`, { state: { id, view: false } })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '9px 20px',
                background: 'linear-gradient(135deg, #fb923c, #f97316)',
                color: '#fff', border: 'none', borderRadius: 'var(--radius-full, 9999px)',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.3)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.2)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
              }}
            >
              <Pencil size={18} /> Edit Schedule
            </button>
          )}
        </div>
      </div>

      <div className="is-split-layout">
        {/* Left Side: Vertical Stepper */}
        <div className="is-sidebar-steps">
          <div
            className={`is-sidebar-step ${step === 1 ? "active" : step > 1 ? "completed" : ""}`}
            onClick={() => setStep(1)}
            style={{ cursor: "pointer" }}
          >
            <div className="is-step-icon">
              {step > 1 ? <Check size={16} /> : 1}
            </div>
            <div className="is-step-text">
              <strong>Session Details</strong>
              <span>Configure basic info</span>
            </div>
          </div>
          <div className="is-step-connector" />
          {showContextStep && (
            <>
              <div
                className={`is-sidebar-step ${step === 2 ? "active" : step > 2 ? "completed" : ""}`}
                onClick={() => setStep(2)}
                style={{ cursor: "pointer" }}
              >
                <div className="is-step-icon">
                  {step > 2 ? <Check size={16} /> : 2}
                </div>
                <div className="is-step-text">
                  <strong>Interview Context</strong>
                  <span>Optional configurations</span>
                </div>
              </div>
              <div className="is-step-connector" />
            </>
          )}
          <div
            className={`is-sidebar-step ${step === (showContextStep ? 3 : 2) ? "active" : step > (showContextStep ? 3 : 2) ? "completed" : ""}`}
            onClick={() => setStep(showContextStep ? 3 : 2)}
            style={{ cursor: "pointer" }}
          >
            <div className="is-step-icon">
              {step > (showContextStep ? 3 : 2) ? <Check size={16} /> : (showContextStep ? 3 : 2)}
            </div>
            <div className="is-step-text">
              <strong>Participants</strong>
              <span>
                {isViewMode
                  ? "View assigned participants"
                  : "Assign participants"}
              </span>
            </div>
          </div>
        </div>

        {/* Right Side: Main Content */}
        <div className="is-main-content">
          {step === 1 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <div
                className="is-form-scrollable"
                style={{
                  paddingRight: "8px",
                  paddingBottom: "24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div
                  className="is-form-card"
                  style={{ flex: "none", height: "auto", minHeight: 0 }}
                >
                  <div
                    className="is-human-panel-header"
                    style={{
                      marginBottom: "16px",
                      borderBottom: "none",
                      paddingBottom: 0,
                    }}
                  >
                    <div
                      className="is-human-panel-icon"
                      style={{ background: "#F3EBFC", color: "#7A3FC2" }}
                    >
                      <ClipboardList size={16} />
                    </div>
                    <h3 style={{ margin: 0 }}>Session Details</h3>
                  </div>

                  <div className="is-interview-type-toggle">
                    <button
                      type="button"
                      className={`is-toggle-btn ${form.interviewType === "AI" ? "active" : ""}`}
                      disabled={isViewMode || isActive || !!id}
                      onClick={() => {
                        setForm((prev) => ({
                          ...prev,
                          interviewType: "AI",
                          videoRecording: false,
                          interviewerEmail: "",
                          meetingLink: "",
                          jobDescription: "",
                        }));
                      }}
                    >
                      <Bot size={18} /> AI Driven Interview
                    </button>
                    <button
                      type="button"
                      className={`is-toggle-btn ${form.interviewType === "EXPERT" ? "active" : ""}`}
                      disabled={isViewMode || isActive || !!id}
                      onClick={() => {
                        setForm((prev) => {
                          const update: any = {
                            interviewType: "EXPERT",
                            videoRecording: true,
                            transcriptionEnabled: true,
                          };
                          return { ...prev, ...update };
                        });
                      }}
                    >
                      <UserRound size={18} /> Expert Driven Interview
                    </button>
                  </div>

                  <div className="is-form-row">
                    <div className="is-form-group">
                      <label>Session Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Frontend Developer - Technical Round"
                        value={form.sessionName}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            sessionName: e.target.value,
                          }))
                        }
                        readOnly={isViewMode}
                        className={isViewMode ? "is-readonly" : ""}
                      />
                    </div>
                    <div className="is-form-group">
                      <label>Round</label>
                      <CustomDropdown
                        options={roundOptions}
                        value={form.roundId}
                        onChange={(val) =>
                          setForm((prev) => ({ ...prev, roundId: val }))
                        }
                        disabled={isViewMode || isActive}
                        placeholder={
                          rounds.length === 0
                            ? "Loading rounds..."
                            : "Select a round"
                        }
                      />
                    </div>
                  </div>

                  <div className="is-form-row">
                    <div className="is-form-group">
                      <label>Duration (minutes)</label>
                      <input
                        type="number"
                        min={15}
                        max={75}
                        step={15}
                        value={form.duration}
                        onChange={(e) => {
                          let val = Number(e.target.value);
                          if (val > 75) val = 75;
                          if (val < 0) val = 0;
                          setForm((prev) => {
                            const update: any = { duration: val };
                            if (prev.startDateTime) {
                              const start = new Date(prev.startDateTime);
                              update.endDateTime = formatLocalDateTimeString(
                                new Date(start.getTime() + val * 60000),
                              );
                            }
                            return { ...prev, ...update };
                          });
                        }}
                        readOnly={isViewMode}
                        className={isViewMode ? "is-readonly" : ""}
                      />
                    </div>
                    <div className="is-form-group">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <label style={{ margin: 0 }}>Time Zone</label>
                        {!(isViewMode || isActive) && (
                          <button
                            type="button"
                            onClick={() => setIsGlobeModalOpen(true)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "var(--primary)",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              padding: 0,
                            }}
                          >
                            <Globe size={14} /> Map
                          </button>
                        )}
                      </div>
                      <TimezoneSelect
                        value={form.timezone}
                        onChange={(tz: any) =>
                          setForm((prev) => ({ ...prev, timezone: tz.value }))
                        }
                        isDisabled={isViewMode || isActive}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            minHeight: "40px",
                            height: "40px",
                            borderRadius: "8px",
                            borderColor: state.isFocused
                              ? "var(--primary)"
                              : "var(--border)",
                            boxShadow: state.isFocused
                              ? "0 0 0 3px var(--primary-ghost)"
                              : "none",
                            "&:hover": {
                              borderColor: "var(--primary)",
                            },
                            fontSize: "0.85rem",
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            fontSize: "0.85rem",
                            backgroundColor: state.isSelected
                              ? "var(--primary-ghost)"
                              : state.isFocused
                                ? "var(--green-50, #f8fafc)"
                                : "transparent",
                            color:
                              state.isSelected || state.isFocused
                                ? "var(--primary)"
                                : "var(--text-heading)",
                            fontWeight: state.isSelected ? 600 : 400,
                            cursor: "pointer",
                            "&:active": {
                              backgroundColor: "var(--primary-ghost)",
                            },
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: "var(--text-heading)",
                            fontWeight: 500,
                            background: "transparent",
                          }),
                          input: (provided) => ({
                            ...provided,
                            color: "var(--text-heading)",
                            margin: 0,
                            padding: 0,
                            "& input": {
                              border: "none !important",
                              boxShadow: "none !important",
                              padding: "0 !important",
                              margin: "0 !important",
                              background: "transparent !important",
                            },
                          }),
                        }}
                      />
                    </div>
                  </div>

                  <div className="is-form-row">
                    <div className="is-form-group">
                      <label>Start Date & Time</label>
                      <DateTimePicker
                        value={form.startDateTime}
                        onChange={(val: string) => {
                          setForm((prev) => {
                            const update: any = { startDateTime: val };
                            if (val) {
                              const start = new Date(val);
                              update.endDateTime = formatLocalDateTimeString(
                                new Date(start.getTime() + prev.duration * 60000),
                              );
                            }
                            return { ...prev, ...update };
                          });
                        }}
                        onLiveChange={(val: string) => {
                          setForm((prev) => {
                            const update: any = {};
                            if (val) {
                              const start = new Date(val);
                              update.endDateTime = formatLocalDateTimeString(
                                new Date(start.getTime() + prev.duration * 60000),
                              );
                            }
                            return { ...prev, ...update };
                          });
                        }}
                        readOnly={isViewMode || isActive}
                        disablePast
                        placeholder="Select start date & time"
                      />
                    </div>
                    <div className="is-form-group">
                      <label>End Date & Time</label>
                      <DateTimePicker
                        value={form.endDateTime}
                        onChange={(val: string) =>
                          setForm((prev) => ({ ...prev, endDateTime: val }))
                        }
                        readOnly={isViewMode}
                        disablePast
                        placeholder="Select end date & time"
                      />
                    </div>
                  </div>

                  {form.interviewType === "AI" && (
                    <div className="is-form-row">
                      <div className="is-form-group">
                        <label>Video Recording</label>
                        <div className="is-toggle-group">
                          <button
                            className={`is-toggle-btn ${form.videoRecording ? "active" : ""}`}
                            onClick={() =>
                              !isViewMode &&
                              setForm((prev) => ({
                                ...prev,
                                videoRecording: true,
                              }))
                            }
                            disabled={isViewMode}
                            type="button"
                          >
                            <Video size={18} /> Yes
                          </button>
                          <button
                            className={`is-toggle-btn ${!form.videoRecording ? "active" : ""}`}
                            onClick={() =>
                              !isViewMode &&
                              setForm((prev) => ({
                                ...prev,
                                videoRecording: false,
                              }))
                            }
                            disabled={isViewMode}
                            type="button"
                          >
                            <VideoOff size={18} /> No
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {form.interviewType === "EXPERT" && (
                  <div className="is-human-panel" style={{ margin: 0 }}>
                    <div className="is-human-panel-header">
                      <div className="is-human-panel-icon">
                        <Video size={16} />
                      </div>
                      <h4 className="is-human-panel-title">
                        Interviewer & Meeting Details
                      </h4>
                    </div>

                    {!id && (
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          marginBottom: "20px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setExpertMeetingOption("create")}
                          style={{
                            padding: "10px 20px",
                            borderRadius: "10px",
                            fontSize: "13px",
                            fontWeight: 600,
                            border:
                              expertMeetingOption === "create"
                                ? "2px solid #7A3FC2"
                                : "1px solid #cbd5e1",
                            backgroundColor:
                              expertMeetingOption === "create"
                                ? "#F3EBFC"
                                : "#ffffff",
                            color:
                              expertMeetingOption === "create"
                                ? "#7A3FC2"
                                : "#64748b",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            boxShadow:
                              expertMeetingOption === "create"
                                ? "0 2px 4px rgba(122, 63, 194, 0.15)"
                                : "none",
                            transition: "all 0.2s",
                          }}
                        >
                          <Video size={16} /> Create Meeting
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpertMeetingOption("add")}
                          style={{
                            padding: "10px 20px",
                            borderRadius: "10px",
                            fontSize: "13px",
                            fontWeight: 600,
                            border:
                              expertMeetingOption === "add"
                                ? "2px solid #7A3FC2"
                                : "1px solid #cbd5e1",
                            backgroundColor:
                              expertMeetingOption === "add"
                                ? "#F3EBFC"
                                : "#ffffff",
                            color:
                              expertMeetingOption === "add"
                                ? "#7A3FC2"
                                : "#64748b",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            boxShadow:
                              expertMeetingOption === "add"
                                ? "0 2px 4px rgba(122, 63, 194, 0.15)"
                                : "none",
                            transition: "all 0.2s",
                          }}
                        >
                          <Link size={16} /> Add Meeting Details
                        </button>
                      </div>
                    )}

                    <div className="is-form-row">
                      <div className="is-form-group" style={{ width: "100%" }}>
                        <label>Interviewer Email(s)</label>
                        <div className="is-input-with-icon">
                          <span className="is-input-icon">
                            <Mail size={16} />
                          </span>
                          <input
                            type="text"
                            placeholder="interviewer1@company.com, interviewer2@company.com"
                            value={form.interviewerEmail}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                interviewerEmail: e.target.value,
                              }))
                            }
                            readOnly={isViewMode}
                            className={isViewMode ? "is-readonly" : ""}
                          />
                        </div>
                      </div>
                    </div>

                    {(id || expertMeetingOption === "add") && (
                      <>
                        <div className="is-form-row">
                          <div
                            className="is-form-group"
                            style={{ width: "100%" }}
                          >
                            <label>Meeting Link</label>
                            <div className="is-input-with-icon">
                              <span className="is-input-icon">
                                <Link size={16} />
                              </span>
                              <input
                                type="url"
                                placeholder="https://meet.google.com/xxx or https://zoom.us/j/xxx"
                                value={form.meetingLink}
                                onChange={(e) =>
                                  setForm((prev) => ({
                                    ...prev,
                                    meetingLink: e.target.value,
                                  }))
                                }
                                readOnly={isViewMode}
                                className={isViewMode ? "is-readonly" : ""}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="is-form-row">
                          <div className="is-form-group">
                            <label>Meeting ID (Optional)</label>
                            <div className="is-input-with-icon">
                              <span className="is-input-icon">
                                <Hash size={16} />
                              </span>
                              <input
                                type="text"
                                placeholder="e.g. 123 456 7890"
                                value={form.meetingId}
                                onChange={(e) =>
                                  setForm((prev) => ({
                                    ...prev,
                                    meetingId: e.target.value,
                                  }))
                                }
                                readOnly={isViewMode}
                                className={isViewMode ? "is-readonly" : ""}
                              />
                            </div>
                          </div>
                          <div className="is-form-group">
                            <label>Meeting Password (Optional)</label>
                            <div className="is-input-with-icon">
                              <span className="is-input-icon">
                                <Lock size={16} />
                              </span>
                              <input
                                type="text"
                                placeholder="e.g. passcode123"
                                value={form.meetingPassword}
                                onChange={(e) =>
                                  setForm((prev) => ({
                                    ...prev,
                                    meetingPassword: e.target.value,
                                  }))
                                }
                                readOnly={isViewMode}
                                className={isViewMode ? "is-readonly" : ""}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    <div
                      className="is-form-group"
                      style={{ marginTop: "16px" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <label style={{ margin: 0 }}>
                          Job Description & Attachment (Optional)
                        </label>
                        {!isViewMode && (
                          <label
                            style={{
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#7A3FC2",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              backgroundColor: "#F3EBFC",
                              transition: "all 0.2s",
                            }}
                          >
                            <Paperclip size={14} /> Attach File
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                              style={{ display: "none" }}
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  setSelectedDescFile(e.target.files[0]);
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>
                      <RichTextEditor
                        value={form.jobDescription}
                        onChange={(content) =>
                          setForm((prev) => ({
                            ...prev,
                            jobDescription: content,
                          }))
                        }
                        readOnly={isViewMode}
                        placeholder="Paste or type the job description here..."
                      />

                      {(selectedDescFile ||
                        form.descriptionFileName ||
                        form.descriptionFilePresignedUrl) && (
                        <div
                          style={{
                            marginTop: "12px",
                            padding: "12px 16px",
                            borderRadius: "10px",
                            backgroundColor: "#1E293B",
                            border: "1px solid #334155",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            color: "#F8FAFC",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <div
                              style={{
                                width: "34px",
                                height: "34px",
                                borderRadius: "8px",
                                backgroundColor: "#7A3FC2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                              }}
                            >
                              <FileText size={18} />
                            </div>
                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: "13px",
                                  color: "#F8FAFC",
                                }}
                              >
                                {selectedDescFile
                                  ? selectedDescFile.name
                                  : form.descriptionFileName ||
                                    "Attached Document"}
                              </div>
                              <div
                                style={{ fontSize: "11px", color: "#94A3B8" }}
                              >
                                {selectedDescFile
                                  ? `${(selectedDescFile.size / 1024).toFixed(1)} KB (Selected)`
                                  : "Description attachment"}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {form.descriptionFilePresignedUrl &&
                              !selectedDescFile && (
                                <a
                                  href={form.descriptionFilePresignedUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    padding: "4px 10px",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "#38BDF8",
                                    backgroundColor: "rgba(56, 189, 248, 0.1)",
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    textDecoration: "none",
                                  }}
                                >
                                  <Eye size={14} /> View Document
                                </a>
                              )}
                            {!isViewMode && (
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedDescFile(null);
                                  setForm((prev) => ({
                                    ...prev,
                                    descriptionFileName: "",
                                    descriptionFileS3Key: "",
                                    descriptionFilePresignedUrl: "",
                                  }));
                                }}
                                style={{
                                  background: "rgba(239, 68, 68, 0.1)",
                                  border: "none",
                                  color: "#EF4444",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  padding: "6px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                title="Remove document"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {(id || expertMeetingOption === "create") && (
                      <div
                        className="is-form-row"
                        style={{ marginTop: "16px" }}
                      >
                        <div className="is-form-group">
                          <label>Do you need to record the meeting?</label>
                          <div className="is-toggle-group">
                            <button
                              className={`is-toggle-btn ${form.videoRecording ? "active" : ""}`}
                              onClick={() =>
                                !isViewMode &&
                                setForm((prev) => ({
                                  ...prev,
                                  videoRecording: true,
                                }))
                              }
                              disabled={isViewMode}
                              type="button"
                            >
                              <Video size={18} /> Yes
                            </button>
                            <button
                              className={`is-toggle-btn ${!form.videoRecording ? "active" : ""}`}
                              onClick={() =>
                                !isViewMode &&
                                setForm((prev) => ({
                                  ...prev,
                                  videoRecording: false,
                                }))
                              }
                              disabled={isViewMode}
                              type="button"
                            >
                              <VideoOff size={18} /> No
                            </button>
                          </div>
                        </div>
                        <div className="is-form-group">
                          <label>
                            Do you need transcription for the meeting?
                          </label>
                          <div className="is-toggle-group">
                            <button
                              className={`is-toggle-btn ${form.transcriptionEnabled ? "active" : ""}`}
                              onClick={() =>
                                !isViewMode &&
                                setForm((prev) => ({
                                  ...prev,
                                  transcriptionEnabled: true,
                                }))
                              }
                              disabled={isViewMode}
                              type="button"
                            >
                              <FileText size={18} /> Yes
                            </button>
                            <button
                              className={`is-toggle-btn ${!form.transcriptionEnabled ? "active" : ""}`}
                              onClick={() =>
                                !isViewMode &&
                                setForm((prev) => ({
                                  ...prev,
                                  transcriptionEnabled: false,
                                }))
                              }
                              disabled={isViewMode}
                              type="button"
                            >
                              <VideoOff size={18} /> No
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {form.interviewType !== "EXPERT" && isCodingRound && (
                  <div
                    className="is-form-card"
                    style={{ flex: "none", height: "auto", minHeight: 0 }}
                  >
                    <div className="is-coding-config">
                      <h4 className="is-coding-config-title">
                        Coding Configuration
                      </h4>

                      {/* Mode Toggle */}
                      {!(isViewMode || isActive) && (
                        <div className="is-mode-toggle">
                          <button
                            type="button"
                            className={`is-toggle-btn ${form.codingMode === "all_languages" ? "active" : ""}`}
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                codingMode: "all_languages",
                              }))
                            }
                          >
                            Free Selection (user picks language)
                          </button>
                          <button
                            type="button"
                            className={`is-toggle-btn ${form.codingMode === "per_language" ? "active" : ""}`}
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                codingMode: "per_language",
                              }))
                            }
                          >
                            Per-Language Count
                          </button>
                        </div>
                      )}
                      {isViewMode && (
                        <div className="is-mode-label">
                          Mode:{" "}
                          {form.codingMode === "per_language"
                            ? "Per-Language Count"
                            : "Free Selection"}
                        </div>
                      )}

                      {form.codingMode === "all_languages" && (
                        <>
                          <div className="is-form-row">
                            <div className="is-form-group">
                              <label>Programming Languages</label>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                {programmingLanguages.map((lang) => (
                                  <button
                                    key={lang}
                                    type="button"
                                    disabled={isViewMode || isActive}
                                    className={`is-toggle-btn ${form.programmingLanguage.includes(lang) ? "active" : ""}`}
                                    onClick={() => {
                                      if (isViewMode || isActive) return;
                                      setForm((prev) => ({
                                        ...prev,
                                        programmingLanguage:
                                          prev.programmingLanguage.includes(
                                            lang,
                                          )
                                            ? prev.programmingLanguage.filter(
                                                (l) => l !== lang,
                                              )
                                            : [
                                                ...prev.programmingLanguage,
                                                lang,
                                              ],
                                      }));
                                    }}
                                  >
                                    {lang}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="is-form-group">
                              <label>Number of Questions</label>
                              <input
                                type="number"
                                min={1}
                                max={20}
                                value={form.noOfCodingQuestion}
                                onChange={(e) => {
                                  let val = Number(e.target.value);
                                  if (val > 20) val = 20;
                                  if (val < 1) val = 1;
                                  setForm((prev) => ({
                                    ...prev,
                                    noOfCodingQuestion: val,
                                  }));
                                }}
                                readOnly={isViewMode || isActive}
                                className={
                                  isViewMode || isActive ? "is-readonly" : ""
                                }
                              />
                            </div>
                          </div>
                          <div className="is-form-row">
                            {(["Easy", "Medium", "Hard"] as const).map(
                              (diff) => (
                                <div className="is-form-group" key={diff}>
                                  <label>{diff} Questions</label>
                                  <input
                                    type="number"
                                    min={0}
                                    max={20}
                                    value={form.difficultyCount[diff] || 0}
                                    onChange={(e) => {
                                      if (isViewMode || isActive) return;
                                      let val = Number(e.target.value);
                                      if (val > 20) val = 20;
                                      if (val < 0) val = 0;
                                      setForm((prev) => ({
                                        ...prev,
                                        difficultyCount: {
                                          ...prev.difficultyCount,
                                          [diff]: val,
                                        },
                                      }));
                                    }}
                                    readOnly={isViewMode || isActive}
                                    className={
                                      isViewMode || isActive
                                        ? "is-readonly"
                                        : ""
                                    }
                                  />
                                </div>
                              ),
                            )}
                          </div>
                        </>
                      )}

                      {form.codingMode === "per_language" && (
                        <div className="filter-config">
                          <div className="filter-config-header">
                            <div className="filter-config-header-col">
                              Language
                            </div>
                            <div className="filter-config-header-col">
                              Question Count
                            </div>
                            <div className="filter-config-header-col">Easy</div>
                            <div className="filter-config-header-col">
                              Medium
                            </div>
                            <div className="filter-config-header-col">Hard</div>
                            <div className="filter-config-header-col is-action" />
                          </div>
                          <div className="filter-config-body">
                            {/* Add row */}
                            {!(isViewMode || isActive) && (
                              <div className="filter-config-row">
                                <div className="filter-config-col">
                                  <CustomDropdown
                                    options={availableLangs.map((l) => ({
                                      value: l,
                                      label: l,
                                    }))}
                                    value={pendingLang}
                                    onChange={setPendingLang}
                                    placeholder="Select language"
                                  />
                                </div>
                                <div className="filter-config-col">
                                  <input
                                    type="number"
                                    min={1}
                                    max={20}
                                    value={pendingCount}
                                    onChange={(e) => {
                                      let val = Number(e.target.value);
                                      if (val > 20) val = 20;
                                      if (val < 1) val = 1;
                                      setPendingCount(val);
                                      setPendingDiff(distributeCount(val));
                                    }}
                                    className="filter-number-input"
                                    placeholder="Count"
                                  />
                                </div>
                                {(["Easy", "Medium", "Hard"] as const).map(
                                  (diff) => (
                                    <div
                                      className="filter-config-col"
                                      key={diff}
                                    >
                                      <input
                                        type="number"
                                        min={0}
                                        max={20}
                                        value={pendingDiff[diff]}
                                        onChange={(e) => {
                                          let val = Number(e.target.value);
                                          if (val > 20) val = 20;
                                          if (val < 0) val = 0;
                                          setPendingDiff((prev) => ({
                                            ...prev,
                                            [diff]: val,
                                          }));
                                        }}
                                        className="filter-number-input"
                                        placeholder="0"
                                      />
                                    </div>
                                  ),
                                )}
                                <div className="filter-config-col is-action">
                                  <button
                                    type="button"
                                    className="filter-add-btn"
                                    onClick={handleAddLangConfig}
                                    disabled={!pendingLang}
                                    title="Add language"
                                  >
                                    <Plus size={16} />
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Added config rows */}
                            {form.languageQuestionConfig.length > 0 ? (
                              form.languageQuestionConfig.map((cfg, idx) => (
                                <div
                                  key={cfg.language}
                                  className="filter-config-row"
                                >
                                  <div className="filter-config-col">
                                    <span className="filter-cell-text">
                                      {cfg.language}
                                    </span>
                                  </div>
                                  <div className="filter-config-col">
                                    <span className="filter-cell-value">
                                      {cfg.question_count}
                                    </span>
                                  </div>
                                  {(["Easy", "Medium", "Hard"] as const).map(
                                    (diff) => (
                                      <div
                                        className="filter-config-col"
                                        key={diff}
                                      >
                                        <span className="filter-cell-value">
                                          {cfg.difficulty_count?.[diff] || 0}
                                        </span>
                                      </div>
                                    ),
                                  )}
                                  <div className="filter-config-col is-action">
                                    {!(isViewMode || isActive) && (
                                      <button
                                        type="button"
                                        className="filter-remove-btn"
                                        onClick={() => {
                                          setForm((prev) => ({
                                            ...prev,
                                            languageQuestionConfig:
                                              prev.languageQuestionConfig.filter(
                                                (_, i) => i !== idx,
                                              ),
                                          }));
                                        }}
                                        title="Remove"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="filter-config-empty">
                                {isViewMode || isActive
                                  ? "No languages configured."
                                  : "Select a language and click + to add."}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* SQL Toggle */}
                      <div className="is-sql-section">
                        <div className="is-sql-row">
                          <label className="is-sql-toggle">
                            <input
                              type="checkbox"
                              checked={form.sqlEnabled}
                              onChange={(e) => {
                                if (!e.target.checked) {
                                  setForm((prev) => ({
                                    ...prev,
                                    sqlEnabled: false,
                                  }));
                                } else {
                                  setForm((prev) => ({
                                    ...prev,
                                    sqlEnabled: true,
                                    sqlDifficultyCount: distributeCount(
                                      prev.sqlQuestionCount,
                                    ),
                                  }));
                                }
                              }}
                              disabled={isViewMode || isActive}
                            />
                            SQL Questions
                          </label>
                          {form.sqlEnabled && (
                            <>
                              <div className="is-sql-field">
                                <label>Count</label>
                                <input
                                  type="number"
                                  min={1}
                                  max={20}
                                  value={form.sqlQuestionCount}
                                  onChange={(e) => {
                                    let val = Number(e.target.value);
                                    if (val > 20) val = 20;
                                    if (val < 1) val = 1;
                                    setForm((prev) => ({
                                      ...prev,
                                      sqlQuestionCount: val,
                                      sqlDifficultyCount: distributeCount(val),
                                    }));
                                  }}
                                  disabled={isViewMode || isActive}
                                  className="filter-number-input"
                                />
                              </div>
                              {(["Easy", "Medium", "Hard"] as const).map(
                                (diff) => (
                                  <div className="is-sql-field" key={diff}>
                                    <label>{diff}</label>
                                    <input
                                      type="number"
                                      min={0}
                                      max={20}
                                      value={form.sqlDifficultyCount[diff]}
                                      onChange={(e) => {
                                        let val = Number(e.target.value);
                                        if (val > 20) val = 20;
                                        if (val < 0) val = 0;
                                        setForm((prev) => ({
                                          ...prev,
                                          sqlDifficultyCount: {
                                            ...prev.sqlDifficultyCount,
                                            [diff]: val,
                                          },
                                        }));
                                      }}
                                      disabled={isViewMode || isActive}
                                      className="filter-number-input"
                                    />
                                  </div>
                                ),
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {!isViewMode && (
                <div
                  className="is-form-actions"
                  style={{
                    justifyContent: "flex-start",
                    gap: "12px",
                    paddingTop: "16px",
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  <button
                    className="btn-ghost"
                    onClick={() => navigate("/dashboard/interviews")}
                    type="button"
                    style={{
                      width: "auto",
                      padding: "8px 16px",
                      height: "auto",
                      fontSize: "0.85rem",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => setStep(2)}
                    type="button"
                    style={{
                      width: "auto",
                      padding: "8px 16px",
                      height: "auto",
                      fontSize: "0.85rem",
                    }}
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 2 && showContextStep && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <div
                className="is-form-scrollable"
                style={{
                  paddingRight: "8px",
                  paddingBottom: "24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div style={{ padding: "24px 28px", background: "#fff", borderRadius: "16px", border: "1px solid #E5E7EB", flex: "none", height: "auto", minHeight: 0 }}>
                  
                  {/* Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <div style={{ background: "#F5EFFB", color: "#8B5CF6", width: "40px", height: "40px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Lightbulb size={20} />
                      </div>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                          <h3 style={{ margin: 0, fontSize: "1.1rem", color: "#111827", fontWeight: 700 }}>Interview Context</h3>
                          <span style={{ background: "#F5EFFB", color: "#8B5CF6", fontSize: "0.75rem", padding: "4px 10px", borderRadius: "16px", fontWeight: 500 }}>AI Evaluation Parameters</span>
                        </div>
                        <p style={{ margin: 0, color: "#6B7280", fontSize: "0.875rem" }}>Customize target competencies, evaluation criteria, and model instructions.</p>
                      </div>
                    </div>
                    <div style={{ background: "#F3F4F6", color: "#6B7280", fontSize: "0.75rem", padding: "6px 12px", borderRadius: "16px", fontWeight: 500 }}>
                      All inputs optional
                    </div>
                  </div>

                  <hr style={{ border: 0, borderTop: "1px solid #F3F4F6", margin: "0 -28px 20px" }} />

                  {/* Job Opportunity Link */}
                  <div style={{ marginBottom: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#111827", fontWeight: 700, fontSize: "0.95rem" }}>
                        <Briefcase size={18} color="#10B981" /> Link to Job Opportunity
                        {!!jobOpportunityId && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#F0FDF4", color: "#059669", fontSize: "0.7rem", fontWeight: 600, padding: "2px 8px", borderRadius: "12px", border: "1px solid #BBF7D0" }}>
                            <Lock size={11} /> Linked from Job
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#9CA3AF" }}>Syncs JD skills automatically</div>
                    </div>
                    <div style={{ display: "flex", gap: "12px", alignItems: "stretch" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <CustomDropdown
                          options={jobOpportunities.map(jo => ({
                            value: jo.opportunity_id,
                            label: jo.title || jo.opportunity_id,
                          }))}
                          value={form.interviewContextJobOpportunityId}
                          placeholder="Select a job opportunity to link (Optional)"
                          searchable
                          disabled={isViewMode || isActive || !!jobOpportunityId}
                          onChange={(val) => {
                            setForm((prev) => {
                              let newSkills = [...prev.interviewContextSkills];
                              if (val) {
                                const jo = jobOpportunities.find(j => j.opportunity_id === val);
                                if (jo) {
                                  const joSkills = [...(jo.skills_required || []), ...(jo.skills_preferred || [])];
                                  joSkills.forEach(s => {
                                    if (!newSkills.some(existing => existing.skill.toLowerCase() === s.toLowerCase())) {
                                      newSkills.push({ skill: s, difficulty: "Medium" }); // Default difficulty
                                    }
                                  });
                                }
                              }
                              return {
                                ...prev,
                                interviewContextJobOpportunityId: val,
                                interviewContextSkills: newSkills,
                              };
                            });
                          }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {selectedJo ? (
                          <button
                            type="button"
                            onClick={() => setIsJdModalOpen(true)}
                            style={{
                              width: "100%",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "10px",
                              padding: "0 14px",
                              background: "#F0FDF4",
                              border: "1px solid #BBF7D0",
                              borderRadius: "10px",
                              cursor: "pointer",
                              textAlign: "left",
                            }}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                              <FileText size={16} color="#059669" style={{ flexShrink: 0 }} />
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: "block", fontSize: "0.83rem", fontWeight: 700, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {selectedJo.title}
                                </span>
                                <span style={{ display: "block", fontSize: "0.72rem", color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {[selectedJo.department, selectedJo.work_location].filter(Boolean).join(" • ") || "Click to view full details"}
                                </span>
                              </span>
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.75rem", fontWeight: 600, color: "#059669", flexShrink: 0 }}>
                              <Eye size={14} /> Preview
                            </span>
                          </button>
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "0 14px",
                              background: "#F9FAFB",
                              border: "1px dashed #D1D5DB",
                              borderRadius: "10px",
                              fontSize: "0.8rem",
                              color: "#9CA3AF",
                            }}
                          >
                            <Eye size={14} /> Select a job to preview details
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {isJdModalOpen && selectedJo && createPortal(
                    <div
                      onClick={() => setIsJdModalOpen(false)}
                      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
                    >
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="jo-modal-pop"
                        style={{ background: "#fff", borderRadius: "20px", maxWidth: "900px", width: "100%", maxHeight: "min(88vh, 720px)", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 70px rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.6)" }}
                      >
                        <div style={{ height: "4px", background: "linear-gradient(90deg, #34D399, #10B981, #0EA5E9)", flexShrink: 0 }} />
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "18px 24px 14px", borderBottom: "1px solid #F1F5F9", flexShrink: 0 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                              <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.2px" }}>{selectedJo.title}</h2>
                              <span style={{ background: "#D1FAE5", color: "#065F46", fontSize: "0.7rem", padding: "3px 10px", borderRadius: "9999px", fontWeight: 700 }}>{selectedJo.status || "Open"}</span>
                            </div>
                            <div style={{ fontSize: "0.78rem", color: "#64748b" }}>
                              {[selectedJo.for_organization_name || selectedJo.company_name, selectedJo.department, selectedJo.work_location].filter(Boolean).join(" • ")}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setIsJdModalOpen(false)}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#FEE2E2"; e.currentTarget.style.color = "#DC2626"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "#F1F5F9"; e.currentTarget.style.color = "#475569"; }}
                            style={{ border: "none", background: "#F1F5F9", borderRadius: "10px", width: "34px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#475569", transition: "all 0.2s ease", flexShrink: 0 }}
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div style={{ flex: "0 1 auto", minHeight: 0, overflow: "hidden", padding: "18px 24px", display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "16px", alignItems: "stretch" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "14px", minWidth: 0, minHeight: 0, overflow: "hidden" }}>
                            <div style={{ border: "1px solid #E5E7EB", borderRadius: "14px", padding: "16px 18px", background: "linear-gradient(180deg, #F8FAFC 0%, #fff 60px)", display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0, maxHeight: "440px" }}>
                              <h3 style={{ margin: 0, fontSize: "0.85rem", fontWeight: 800, color: "#047857", letterSpacing: "0.2px" }}>Job Description</h3>
                              <div style={{ width: "28px", height: "3px", borderRadius: "9999px", background: "linear-gradient(90deg, #34D399, #0EA5E9)", margin: "8px 0 10px", flexShrink: 0 }} />
                              <div className="jd-preview-content jo-modal-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: "8px" }}>
                                {selectedJo.description ? (
                                  <div dangerouslySetInnerHTML={{ __html: selectedJo.description }} style={{ fontSize: "0.83rem", color: "#4B5563", lineHeight: "1.65" }} />
                                ) : (
                                  <div style={{ fontSize: "0.83rem", color: "#9CA3AF", fontStyle: "italic" }}>No description provided.</div>
                                )}
                              </div>
                            </div>
                            {((selectedJo.skills_required?.length || 0) > 0 || (selectedJo.skills_preferred?.length || 0) > 0) && (
                              <div style={{ border: "1px solid #E5E7EB", borderRadius: "14px", padding: "14px 18px", background: "#fff", flexShrink: 0, maxHeight: "168px", overflowY: "auto" }} className="jo-modal-scroll">
                                <h3 style={{ margin: 0, fontSize: "0.85rem", fontWeight: 800, color: "#7C3AED", letterSpacing: "0.2px" }}>Skills & Technologies</h3>
                                <div style={{ width: "28px", height: "3px", borderRadius: "9999px", background: "linear-gradient(90deg, #A78BFA, #8B5CF6)", margin: "8px 0 10px" }} />
                                {(selectedJo.skills_required?.length || 0) > 0 && (
                                  <div style={{ marginBottom: (selectedJo.skills_preferred?.length || 0) > 0 ? "10px" : 0 }}>
                                    <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#059669", marginBottom: "6px" }}>● Required</div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                      {selectedJo.skills_required.map((s: string, i: number) => (
                                        <span key={i} style={{ background: "#D1FAE5", color: "#065F46", fontSize: "0.75rem", fontWeight: 600, padding: "4px 10px", borderRadius: "9999px" }}>{s}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {(selectedJo.skills_preferred?.length || 0) > 0 && (
                                  <div>
                                    <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#7C3AED", marginBottom: "6px" }}>● Preferred</div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                      {selectedJo.skills_preferred.map((s: string, i: number) => (
                                        <span key={i} style={{ background: "#EDE9FE", color: "#6D28D9", fontSize: "0.75rem", fontWeight: 600, padding: "4px 10px", borderRadius: "9999px" }}>{s}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "14px", minWidth: 0, minHeight: 0, overflow: "hidden" }}>
                            <div style={{ border: "1px solid #E5E7EB", borderRadius: "14px", padding: "16px 18px", background: "#fff", flexShrink: 0 }}>
                              <h3 style={{ margin: 0, fontSize: "0.85rem", fontWeight: 800, color: "#0369A1", letterSpacing: "0.2px" }}>Logistics & Compensation</h3>
                              <div style={{ width: "28px", height: "3px", borderRadius: "9999px", background: "linear-gradient(90deg, #38BDF8, #0369A1)", margin: "8px 0 12px" }} />
                              <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "0.8rem" }}>
                                {selectedJo.department && (
                                  <div style={{ display: "grid", gridTemplateColumns: "132px 1fr", gap: "8px", alignItems: "baseline" }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: "6px", color: "#64748b" }}><Briefcase size={14} /> Department</span>
                                    <span style={{ fontWeight: 600, color: "#0f172a", textAlign: "left" }}>{selectedJo.department}</span>
                                  </div>
                                )}
                                {selectedJo.work_location && (
                                  <div style={{ display: "grid", gridTemplateColumns: "132px 1fr", gap: "8px", alignItems: "baseline" }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: "6px", color: "#64748b" }}><Globe size={14} /> Location</span>
                                    <span style={{ fontWeight: 600, color: "#0f172a", textAlign: "left" }}>{selectedJo.work_location}</span>
                                  </div>
                                )}
                                {selectedJo.hiring_type && (
                                  <div style={{ display: "grid", gridTemplateColumns: "132px 1fr", gap: "8px", alignItems: "baseline" }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: "6px", color: "#64748b" }}><Check size={14} /> Hiring Type</span>
                                    <span style={{ fontWeight: 600, color: "#0f172a", textAlign: "left" }}>{selectedJo.hiring_type}</span>
                                  </div>
                                )}
                                {selectedJo.payment_type && (selectedJo.min_ctc || selectedJo.max_ctc || selectedJo.min_hourly_rate) && (
                                  <div style={{ display: "grid", gridTemplateColumns: "132px 1fr", gap: "8px", alignItems: "baseline" }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: "6px", color: "#64748b" }}><Hash size={14} /> Compensation</span>
                                    <span style={{ fontWeight: 700, color: "#059669", textAlign: "left" }}>
                                      {selectedJo.payment_type === "Hourly"
                                        ? `$${(selectedJo.min_hourly_rate || 0).toLocaleString()} - $${(selectedJo.max_hourly_rate || 0).toLocaleString()}/hr`
                                        : `$${(selectedJo.min_ctc || 0).toLocaleString()} - $${(selectedJo.max_ctc || 0).toLocaleString()}`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ border: "1px solid #E5E7EB", borderRadius: "14px", padding: "16px 18px", background: "#fff", flex: "1 1 auto", minHeight: 0, overflowY: "auto" }} className="jo-modal-scroll">
                              <h3 style={{ margin: 0, fontSize: "0.85rem", fontWeight: 800, color: "#B45309", letterSpacing: "0.2px" }}>Role Requirements</h3>
                              <div style={{ width: "28px", height: "3px", borderRadius: "9999px", background: "linear-gradient(90deg, #FBBF24, #B45309)", margin: "8px 0 12px" }} />
                              <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "0.8rem" }}>
                                {selectedJo.job_type && (
                                  <div style={{ display: "grid", gridTemplateColumns: "132px 1fr", gap: "8px", alignItems: "baseline" }}>
                                    <span style={{ color: "#64748b" }}>Job Type</span>
                                    <span style={{ fontWeight: 600, color: "#0f172a" }}>{selectedJo.job_type}</span>
                                  </div>
                                )}
                                {selectedJo.work_type && (
                                  <div style={{ display: "grid", gridTemplateColumns: "132px 1fr", gap: "8px", alignItems: "baseline" }}>
                                    <span style={{ color: "#64748b" }}>Work Type</span>
                                    <span style={{ fontWeight: 600, color: "#0f172a" }}>{selectedJo.work_type}</span>
                                  </div>
                                )}
                                {(selectedJo.min_years_experience !== undefined || selectedJo.max_years_experience) && (
                                  <div style={{ display: "grid", gridTemplateColumns: "132px 1fr", gap: "8px", alignItems: "baseline" }}>
                                    <span style={{ color: "#64748b" }}>Experience</span>
                                    <span style={{ fontWeight: 600, color: "#0f172a" }}>{selectedJo.min_years_experience || 0}{selectedJo.max_years_experience ? ` - ${selectedJo.max_years_experience}` : "+"} years</span>
                                  </div>
                                )}
                                {selectedJo.education_level && (
                                  <div style={{ display: "grid", gridTemplateColumns: "132px 1fr", gap: "8px", alignItems: "baseline" }}>
                                    <span style={{ color: "#64748b" }}>Education</span>
                                    <span style={{ fontWeight: 600, color: "#0f172a", textAlign: "left" }}>{selectedJo.education_level}</span>
                                  </div>
                                )}
                                {selectedJo.vacancies && (
                                  <div style={{ display: "grid", gridTemplateColumns: "132px 1fr", gap: "8px", alignItems: "baseline" }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: "6px", color: "#64748b" }}><Users size={14} /> Vacancies</span>
                                    <span style={{ fontWeight: 600, color: "#0f172a" }}>{selectedJo.vacancies}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}

                  <hr style={{ border: 0, borderTop: "1px solid #F3F4F6", margin: "0 -28px 20px" }} />

                  {/* Target Skills */}
                  <div style={{ marginBottom: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#111827", fontWeight: 700, fontSize: "0.95rem" }}>
                        <Code size={18} color="#10B981" /> Target Skills & Difficulty
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#9CA3AF" }}>Skills are weighted heavily by AI agent</div>
                    </div>
                    
                    <div style={{ display: "flex", gap: "10px", alignItems: "stretch", marginBottom: "16px" }}>
                      <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
                        <input
                          type="text"
                          className="is-input"
                          style={{ flex: 1, width: "100%", height: "44px", padding: "0 14px", borderRadius: "10px", border: "1px solid #D1D5DB", fontSize: "0.875rem", outline: "none", transition: "border-color 0.2s ease, box-shadow 0.2s ease", boxSizing: "border-box" }}
                          placeholder="Enter skill (e.g. Distributed Systems, Kubernetes, Go)"
                          id="context-skill-input"
                          value={skillInput}
                          onChange={(e) => setSkillInput(e.target.value)}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = "#10B981";
                            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(16,185,129,0.15)";
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = "#D1D5DB";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              document.getElementById("context-skill-add")?.click();
                            }
                            if (e.key === "Escape" && editingSkillIdx !== null) {
                              setEditingSkillIdx(null);
                              setSkillInput("");
                              setSkillDiff("Medium");
                            }
                          }}
                          disabled={isViewMode || isActive}
                        />
                      </div>

                      <div style={{ flex: 1, minWidth: 0, display: "flex", gap: "10px", alignItems: "center" }}>
                      {/* Custom Difficulty Selector */}
                      <div style={{ display: "flex", alignItems: "center", background: "#fff", borderRadius: "9999px", padding: "4px", border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", height: "44px", boxSizing: "border-box", flexShrink: 0 }}>
                        {[
                          { label: "Easy", bg: "#DCFCE7", text: "#15803D", dot: "#22C55E", border: "#BBF7D0" },
                          { label: "Medium", bg: "#FEF3C7", text: "#B45309", dot: "#F59E0B", border: "#FDE68A" },
                          { label: "Hard", bg: "#FEE2E2", text: "#B91C1C", dot: "#EF4444", border: "#FECACA" },
                        ].map(({ label: diff, bg, text, dot, border }) => {
                          const isSelected = skillDiff === diff;
                          return (
                            <button
                              key={diff}
                              type="button"
                              onClick={() => setSkillDiff(diff)}
                              style={{
                                padding: "6px 14px",
                                border: `1px solid ${isSelected ? border : "transparent"}`,
                                borderRadius: "9999px",
                                background: isSelected ? bg : "transparent",
                                boxShadow: isSelected ? "0 1px 3px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.6)" : "none",
                                color: isSelected ? text : "#6B7280",
                                fontWeight: isSelected ? "600" : "400",
                                fontSize: "0.8rem",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                              data-selected={isSelected ? "true" : "false"}
                              className="skill-diff-btn"
                            >
                              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: isSelected ? dot : "#D1D5DB" }} />
                              {diff}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        id="context-skill-add"
                        disabled={isViewMode || isActive}
                        onClick={() => {
                          const skillName = skillInput.trim();
                          if (!skillName) return;
                          const diffToAdd = skillDiff;
                          if (editingSkillIdx !== null) {
                            setForm(prev => {
                              const mapped = prev.interviewContextSkills.map((s, i) =>
                                i === editingSkillIdx ? { skill: skillName, difficulty: diffToAdd } : s
                              );
                              const firstIdx = new Map<string, number>();
                              mapped.forEach((s, i) => {
                                const k = s.skill.toLowerCase();
                                if (!firstIdx.has(k)) firstIdx.set(k, i);
                              });
                              return {
                                ...prev,
                                interviewContextSkills: mapped.filter((s, i) => firstIdx.get(s.skill.toLowerCase()) === i),
                              };
                            });
                            setEditingSkillIdx(null);
                          } else {
                            setForm(prev => {
                              if (prev.interviewContextSkills.some(s => s.skill.toLowerCase() === skillName.toLowerCase())) return prev;
                              return {
                                ...prev,
                                interviewContextSkills: [...prev.interviewContextSkills, { skill: skillName, difficulty: diffToAdd }]
                              };
                            });
                          }
                          setSkillInput("");
                        }}
                        onMouseEnter={(e) => {
                          const btn = e.currentTarget;
                          btn.style.transform = "translateY(-1px)";
                          btn.style.boxShadow = "0 4px 12px rgba(16,185,129,0.3), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 4px rgba(0,0,0,0.08)";
                          const shine = btn.querySelector(".add-skill-shine") as HTMLElement | null;
                          if (shine) { shine.style.left = "130%"; }
                        }}
                        onMouseLeave={(e) => {
                          const btn = e.currentTarget;
                          btn.style.transform = "translateY(0)";
                          btn.style.boxShadow = "0 2px 8px rgba(16,185,129,0.22), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.06)";
                          const shine = btn.querySelector(".add-skill-shine") as HTMLElement | null;
                          if (shine) { shine.style.left = "-60%"; }
                        }}
                        onMouseDown={(e) => { e.currentTarget.style.transform = "translateY(0) scale(0.97)"; }}
                        onMouseUp={(e) => { e.currentTarget.style.transform = "translateY(-2px) scale(1)"; }}
                        style={{
                          position: "relative",
                          overflow: "hidden",
                          background: "linear-gradient(180deg, #2DD4A7 0%, #10B981 45%, #059669 100%)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "9999px",
                          padding: "10px 28px",
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          letterSpacing: "0.3px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          boxShadow: "0 2px 8px rgba(16,185,129,0.22), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.06)",
                          transition: "all 0.22s ease",
                          whiteSpace: "nowrap",
                          height: "44px",
                          boxSizing: "border-box",
                          flexShrink: 0,
                        }}
                      >
                        <span className="add-skill-shine" style={{ position: "absolute", top: 0, bottom: 0, left: "-60%", width: "45%", background: "linear-gradient(105deg, transparent, rgba(255,255,255,0.4), transparent)", transform: "skewX(-20deg)", transition: "left 0.6s ease", pointerEvents: "none" }} />
                        {editingSkillIdx !== null ? <Check size={16} strokeWidth={3} /> : <Plus size={16} strokeWidth={3} />}
                        {editingSkillIdx !== null ? "Update Skill" : "Add Skill"}
                      </button>
                      {editingSkillIdx !== null && !isViewMode && !isActive && (
                        <button
                          type="button"
                          title="Cancel edit and add a new skill"
                          onClick={() => { setEditingSkillIdx(null); setSkillInput(""); setSkillDiff("Medium"); }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#FEF2F2"; e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.borderColor = "#FECACA"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#6B7280"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
                          style={{ height: "44px", width: "44px", borderRadius: "50%", border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s ease", flexShrink: 0, boxSizing: "border-box" }}
                        >
                          <X size={16} strokeWidth={2.5} />
                        </button>
                      )}
                      </div>
                    </div>
                    
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                      {form.interviewContextSkills.map((s, idx) => {
                        const level = (s.difficulty || "Medium").toLowerCase();
                        const chip = level === "easy"
                          ? { bg: "#DCFCE7", border: "#BBF7D0", text: "#15803D", dot: "#22C55E" }
                          : level === "hard"
                          ? { bg: "#FEE2E2", border: "#FECACA", text: "#B91C1C", dot: "#EF4444" }
                          : { bg: "#FFFBEB", border: "#FDE68A", text: "#92400E", dot: "#F59E0B" };
                        const chipBg2 = level === "easy" ? "#DCFCE7" : level === "hard" ? "#FEE2E2" : "#FEF3C7";
                        const isEditing = editingSkillIdx === idx;
                        return (
                          <div
                            key={idx}
                            title={isViewMode || isActive ? s.skill : "Click to edit"}
                            onClick={() => {
                              if (isViewMode || isActive) return;
                              if (editingSkillIdx === idx) {
                                setEditingSkillIdx(null);
                                setSkillInput("");
                                setSkillDiff("Medium");
                                return;
                              }
                              setSkillInput(s.skill);
                              setSkillDiff(s.difficulty || "Medium");
                              setEditingSkillIdx(idx);
                              document.getElementById("context-skill-input")?.focus();
                            }}
                            onMouseEnter={(e) => {
                              const el = e.currentTarget;
                              el.style.transform = "translateY(-2px) scale(1.02)";
                              el.style.boxShadow = `0 6px 16px ${chip.dot}45, 0 2px 6px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)`;
                              el.style.borderColor = chip.dot;
                            }}
                            onMouseLeave={(e) => {
                              const el = e.currentTarget;
                              el.style.transform = "translateY(0) scale(1)";
                              el.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
                              el.style.borderColor = isEditing ? chip.dot : chip.border;
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              background: `linear-gradient(135deg, ${chip.bg}, ${chipBg2})`,
                              border: `1px solid ${isEditing ? chip.dot : chip.border}`,
                              outline: isEditing ? `2px solid ${chip.dot}59` : "none",
                              padding: "6px 8px 6px 12px",
                              borderRadius: "10px",
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              color: chip.text,
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                              cursor: isViewMode || isActive ? "default" : "pointer",
                              transition: "all 0.2s ease",
                            }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              {s.skill}
                              <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "0.72rem", fontWeight: 700, marginLeft: "4px", background: "rgba(255,255,255,0.65)", backdropFilter: "blur(4px)", padding: "2px 8px", borderRadius: "12px", border: `1px solid ${chip.border}`, color: chip.text }}>
                                <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: chip.dot, boxShadow: `0 0 6px ${chip.dot}` }}></div>
                                {s.difficulty || "Medium"}
                              </div>
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setForm(prev => ({ ...prev, interviewContextSkills: prev.interviewContextSkills.filter((_, i) => i !== idx) }));
                                setEditingSkillIdx(prevEdit => {
                                  if (prevEdit === null) return prevEdit;
                                  if (prevEdit === idx) { setSkillInput(""); return null; }
                                  return prevEdit > idx ? prevEdit - 1 : prevEdit;
                                });
                              }}
                              disabled={isViewMode || isActive}
                              title="Remove skill"
                              onMouseEnter={(e) => {
                                const b = e.currentTarget;
                                b.style.background = "rgba(0,0,0,0.08)";
                                b.style.opacity = "1";
                              }}
                              onMouseLeave={(e) => {
                                const b = e.currentTarget;
                                b.style.background = "transparent";
                                b.style.opacity = "0.55";
                              }}
                              style={{ background: "transparent", border: "none", marginLeft: "8px", cursor: "pointer", color: chip.text, display: "flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", borderRadius: "50%", padding: 0, opacity: 0.55, transition: "all 0.2s ease", flexShrink: 0 }}
                            >
                              <X size={13} strokeWidth={2.5} />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <hr style={{ border: 0, borderTop: "1px solid #F3F4F6", margin: "0 -28px 20px" }} />

                  {/* Deep Dive Mode */}
                  <div style={{ marginBottom: "20px", padding: "14px 18px", background: "#F0FDF4", borderRadius: "10px", border: "1px solid #BBF7D0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px", flexWrap: "wrap" }}>
                          <h4 style={{ margin: 0, fontSize: "0.88rem", color: "#111827", fontWeight: 700 }}>Senior Candidate Deep-Dive Mode</h4>
                          <span style={{ background: "#D1FAE5", color: "#065F46", fontSize: "0.7rem", padding: "3px 10px", borderRadius: "9999px", fontWeight: 600 }}>Recommended for Lead Roles</span>
                        </div>
                        <p style={{ margin: 0, fontSize: "0.8rem", color: "#6B7280", lineHeight: "1.55" }}>
                          If a candidate has 5+ years of experience, the AI uses real-world trade-off scenarios, distributed concurrency challenges, and high-availability architecture problems by default, replacing syntax-based questions. You can manually disable this feature if desired.
                        </p>
                      </div>
                      <div 
                        onClick={() => !isViewMode && !isActive && setForm(prev => ({ ...prev, interviewContextSeniorDeepDive: !prev.interviewContextSeniorDeepDive }))} 
                        style={{ 
                          width: "44px", 
                          height: "24px", 
                          background: form.interviewContextSeniorDeepDive ? "#059669" : "#D1D5DB", 
                          borderRadius: "9999px",
                          position: "relative",
                          cursor: isViewMode || isActive ? "default" : "pointer",
                          transition: "background 0.25s ease",
                          flexShrink: 0,
                          boxShadow: form.interviewContextSeniorDeepDive ? "0 2px 8px rgba(5,150,105,0.35)" : "none",
                        }}
                      >
                        <div style={{
                          width: "18px",
                          height: "18px",
                          background: "#fff",
                          borderRadius: "50%",
                          position: "absolute",
                          top: "3px",
                          left: form.interviewContextSeniorDeepDive ? "23px" : "3px",
                          transition: "left 0.25s ease",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                        }}></div>
                      </div>
                    </div>
                  </div>

                  <hr style={{ border: 0, borderTop: "1px solid #F3F4F6", margin: "0 -28px 20px" }} />

                  {/* Custom Instructions */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#111827", fontWeight: 700, fontSize: "0.95rem" }}>
                        <span style={{ width: "28px", height: "28px", borderRadius: "8px", background: "#ECFDF5", border: "1px solid #BBF7D0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <MessageSquare size={15} color="#059669" />
                        </span>
                        Instructions to AI Interviewer
                      </div>
                      {form.interviewContextCustomInstructions && !isViewMode && !isActive && (
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, interviewContextCustomInstructions: "" }))}
                          onMouseEnter={(e) => { e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.borderColor = "#FECACA"; e.currentTarget.style.background = "#FEF2F2"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = "#6B7280"; e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.background = "transparent"; }}
                          style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.75rem", fontWeight: 600, color: "#6B7280", background: "transparent", border: "1px solid #E5E7EB", borderRadius: "9999px", padding: "4px 12px", cursor: "pointer", transition: "all 0.2s ease" }}
                        >
                          <X size={12} strokeWidth={2.5} /> Clear
                        </button>
                      )}
                    </div>
                    <div style={{ position: "relative" }}>
                      <textarea
                        className="is-input"
                        style={{ width: "100%", padding: "14px 16px", borderRadius: "12px", border: "1px solid #E5E7EB", background: "#F9FAFB", minHeight: "110px", resize: "vertical", fontSize: "0.85rem", lineHeight: "1.65", color: "#1F2937", outline: "none", transition: "all 0.2s ease", boxShadow: "inset 0 1px 3px rgba(0,0,0,0.04)", boxSizing: "border-box" }}
                        placeholder=""
                        value={form.interviewContextCustomInstructions}
                        onChange={(e) => setForm(prev => ({ ...prev, interviewContextCustomInstructions: e.target.value.slice(0, 1000) }))}
                        onFocus={(e) => {
                          e.currentTarget.style.background = "#fff";
                          e.currentTarget.style.borderColor = "#10B981";
                          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(16,185,129,0.12), inset 0 1px 3px rgba(0,0,0,0.03)";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.background = "#F9FAFB";
                          e.currentTarget.style.borderColor = "#E5E7EB";
                          e.currentTarget.style.boxShadow = "inset 0 1px 3px rgba(0,0,0,0.04)";
                        }}
                        disabled={isViewMode || isActive}
                      />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", flexWrap: "wrap", gap: "8px" }}>
                      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "0.75rem", color: "#9CA3AF", marginRight: "4px", fontWeight: 500 }}>Quick Add:</span>
                        {[
                          { label: "+ Probe with follow-ups", text: "Ask at least two probing follow-up questions for every answer to test depth of understanding, not recall.", bg: "#E0F2FE", textColor: "#0369A1", border: "#BAE6FD", hoverBg: "#BAE6FD" },
                          { label: "+ Real production scenarios", text: "Frame questions around real production scenarios and incidents instead of textbook definitions.", bg: "#EDE9FE", textColor: "#6D28D9", border: "#DDD6FE", hoverBg: "#DDD6FE" },
                          { label: "+ Assess communication", text: "Evaluate how clearly the candidate explains their reasoning, trade-offs, and decisions.", bg: "#FEF3C7", textColor: "#B45309", border: "#FDE68A", hoverBg: "#FDE68A" },
                          { label: "+ Build on answers", text: "Do not repeat covered ground; build each new question on the candidate's previous answers.", bg: "#D1FAE5", textColor: "#065F46", border: "#A7F3D0", hoverBg: "#A7F3D0" },
                        ].map(({ label: pill, text: snippet, bg, textColor, border, hoverBg }) => (
                          <button
                            key={pill}
                            type="button"
                            disabled={isViewMode || isActive}
                            title={snippet}
                            onClick={() => {
                              setForm(prev => {
                                const current = prev.interviewContextCustomInstructions.trim();
                                const nextStr = current ? `${current} ${snippet}` : `${snippet}`;
                                return { ...prev, interviewContextCustomInstructions: nextStr.slice(0, 1000) };
                              });
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = hoverBg;
                              e.currentTarget.style.transform = "translateY(-1px)";
                              e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = bg;
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                            style={{
                              background: bg,
                              color: textColor,
                              border: `1px solid ${border}`,
                              borderRadius: "9999px",
                              padding: "6px 14px",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                          >
                            {pill}
                          </button>
                        ))}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ width: "90px", height: "4px", borderRadius: "9999px", background: "#E5E7EB", overflow: "hidden" }}>
                          <div style={{ width: `${Math.min(100, (form.interviewContextCustomInstructions.length / 1000) * 100)}%`, height: "100%", borderRadius: "9999px", background: form.interviewContextCustomInstructions.length > 900 ? "#EF4444" : form.interviewContextCustomInstructions.length > 700 ? "#F59E0B" : "linear-gradient(90deg, #34D399, #059669)", transition: "width 0.25s ease" }} />
                        </div>
                        <div style={{ fontSize: "0.75rem", color: form.interviewContextCustomInstructions.length > 900 ? "#DC2626" : "#9CA3AF", fontWeight: 500, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {form.interviewContextCustomInstructions.length} / 1000 characters
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {!isViewMode && (
                <div
                  className="is-form-actions"
                  style={{
                    justifyContent: "flex-start",
                    gap: "12px",
                    paddingTop: "16px",
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  <button
                    className="btn-ghost"
                    onClick={() => setStep(1)}
                    type="button"
                    style={{
                      width: "auto",
                      padding: "8px 16px",
                      height: "auto",
                      fontSize: "0.85rem",
                    }}
                  >
                    <ArrowLeft size={16} /> Back
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => setStep(3)}
                    type="button"
                    style={{
                      width: "auto",
                      padding: "8px 16px",
                      height: "auto",
                      fontSize: "0.85rem",
                    }}
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}

          {step === (showContextStep ? 3 : 2) && (
            <div
              className="is-form-card"
              style={{
                padding: 0,
                border: "none",
                boxShadow: "none",
                background: "transparent",
              }}
            >
              {/* Table Panel */}
              <div className="lp-table-panel">
                <div
                  className="lp-table-panel-head"
                  style={{ padding: "14px 24px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <h2
                      style={{
                        margin: 0,
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: "var(--text-heading)",
                      }}
                    >
                      {isViewMode
                        ? "Assigned Participants"
                        : "Select Participants"}
                    </h2>
                    <span
                      className="lp-stat-badge active"
                      style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                    >
                      <span className="lp-active-dot" />
                      {isViewMode
                        ? selectedUsers.length > 0
                          ? selectedUsers.length
                          : companyUsers.length
                        : selectedUsers.length}{" "}
                      Participant
                      {(isViewMode
                        ? selectedUsers.length || companyUsers.length
                        : selectedUsers.length) !== 1
                        ? "s"
                        : ""}
                    </span>
                  </div>

                  {!isViewMode && (
                    <div
                      className="lp-search"
                      style={{
                        height: "36px",
                        width: "240px",
                        background: "var(--surface-alt)",
                      }}
                    >
                      <Search size={14} />
                      <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchUser}
                        onChange={(e) => setSearchUser(e.target.value)}
                        style={{ fontSize: "0.8rem" }}
                      />
                      {searchUser && (
                        <button
                          style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            color: "#94a3b8",
                            padding: 0,
                          }}
                          onClick={() => setSearchUser("")}
                        >
                          <svg
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="lp-table-scroll-area" onScroll={handleScroll}>
                  {isViewMode ? (
                    // ── View mode: show assigned participants ──
                    (() => {
                      const assignedList =
                        selectedUsers.length > 0
                          ? selectedUsers.map((email) => {
                              const user = companyUsers.find(
                                (u) => u.email === email,
                              );
                              return (
                                user || {
                                  id: email,
                                  name: email,
                                  email,
                                  role: "",
                                  department: "",
                                }
                              );
                            })
                          : companyUsers;
                      return assignedList.length === 0 ? (
                        <div
                          className="is-empty"
                          style={{ padding: "40px 20px" }}
                        >
                          <Users
                            size={40}
                            color="#cbd5e1"
                            style={{ margin: "0 auto" }}
                          />
                          <h3
                            style={{ margin: "12px 0 4px", fontSize: "1rem" }}
                          >
                            No Participants Assigned
                          </h3>
                          <p style={{ fontSize: "0.8rem" }}>
                            No participants were assigned to this session.
                          </p>
                        </div>
                      ) : (
                        <Table className="learner-table">
                          <TableHeader>
                            <TableRow>
                              <TableHead>#</TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Email Address</TableHead>
                              <TableHead>Educational Qualification</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {assignedList.map((u, idx) => (
                              <TableRow key={u.id}>
                                <TableCell
                                  style={{ color: "#94a3b8", fontWeight: 600 }}
                                >
                                  {idx + 1}
                                </TableCell>
                                <TableCell>
                                  <strong style={{ color: "#0f172a" }}>
                                    {u.name || u.email}
                                  </strong>
                                </TableCell>
                                <TableCell>
                                  <span style={{ color: "#475569" }}>
                                    {u.email}
                                  </span>
                                </TableCell>
                                <TableCell>{u.department || "-"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      );
                    })()
                  ) : // ── Edit / Create mode: selectable table ──
                  filteredUsers.length === 0 ? (
                    <div className="is-empty" style={{ padding: "40px 20px" }}>
                      <Users
                        size={40}
                        color="#cbd5e1"
                        style={{ margin: "0 auto" }}
                      />
                      <h3 style={{ margin: "12px 0 4px", fontSize: "1rem" }}>
                        No users found
                      </h3>
                      <p style={{ fontSize: "0.8rem" }}>
                        {searchUser
                          ? "Try adjusting your search"
                          : "No participants available."}
                      </p>
                    </div>
                  ) : (
                    <Table className="learner-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead style={{ width: 40, paddingLeft: 20 }}>
                            {form.interviewType !== "EXPERT" && (
                              <input
                                type="checkbox"
                                className="lp-checkbox"
                                onChange={toggleAll}
                                checked={
                                  filteredUsers.length > 0 &&
                                  filteredUsers.every((u) =>
                                    selectedUsers.includes(u.email),
                                  )
                                }
                              />
                            )}
                          </TableHead>
                          <TableHead>First Name</TableHead>
                          <TableHead>Last Name</TableHead>
                          <TableHead>Email Address</TableHead>
                          <TableHead>Educational Qualification</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.map((u) => {
                          const [firstName, ...rest] = u.name.split(" ");
                          const lastName = rest.join(" ");
                          return (
                            <TableRow
                              key={u.id}
                              data-state={
                                selectedUsers.includes(u.email)
                                  ? "selected"
                                  : undefined
                              }
                            >
                              <TableCell style={{ paddingLeft: 20 }}>
                                <input
                                  type={
                                    form.interviewType === "EXPERT"
                                      ? "radio"
                                      : "checkbox"
                                  }
                                  className="lp-checkbox"
                                  checked={selectedUsers.includes(u.email)}
                                  onChange={() => toggleUser(u.email)}
                                />
                              </TableCell>
                              <TableCell>
                                <strong style={{ color: "#0f172a" }}>
                                  {firstName || "-"}
                                </strong>
                              </TableCell>
                              <TableCell>{lastName || "-"}</TableCell>
                              <TableCell>
                                <span style={{ color: "#475569" }}>
                                  {u.email}
                                </span>
                              </TableCell>
                              <TableCell>{u.department || "-"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                  {loadingMore && (
                    <div
                      style={{
                        padding: "16px",
                        textAlign: "center",
                        color: "#64748b",
                        fontSize: "0.85rem",
                      }}
                    >
                      Loading more users...
                    </div>
                  )}
                </div>
              </div>

              {!isViewMode && (
                <div
                  className="is-form-actions"
                  style={{
                    marginTop: "16px",
                    background: "var(--surface)",
                    padding: "16px 20px",
                    borderRadius: "var(--radius-xl)",
                    border: "1px solid var(--border)",
                    display: "flex",
                    justifyContent: "flex-start",
                    gap: "12px",
                  }}
                >
                  <button
                    className="btn-ghost"
                    onClick={() => setStep(1)}
                    type="button"
                    style={{
                      width: "auto",
                      padding: "8px 16px",
                      height: "auto",
                      fontSize: "0.85rem",
                    }}
                  >
                    Back
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleSubmit}
                    type="button"
                    style={{
                      width: "auto",
                      padding: "8px 16px",
                      height: "auto",
                      fontSize: "0.85rem",
                    }}
                  >
                    {id ? "Update Schedule" : "Create Schedule"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Action Modals */}
      <GlobeTimezoneModal
        isOpen={isGlobeModalOpen}
        onClose={() => setIsGlobeModalOpen(false)}
        onSelectTimezone={(tz) =>
          setForm((prev) => ({ ...prev, timezone: tz }))
        }
      />
    </motion.div>
  );
};

export default ScheduleInterviewPage;
